// # 파일 올리기 — 자리를 받고, 스토리지에 직접 PUT 하고, id 를 돌려준다
//
// 서버는 바이트를 보지 않는다(ADR 0006). 자리는 60초짜리라 **받자마자 올린다**.
// 선언한 크기와 다른 몸통은 스토리지가 거절한다 — 그래서 `file.size` 를 그대로 선언한다.

import type { ChannelId } from '@maru/protocol'
import { ATTACHMENT_MAX_BYTES, FACE_KINDS, FACE_MAX_BYTES } from '@maru/protocol'
import type { Attachment, MaruApi } from './api.ts'

export interface Uploadable {
  name: string
  type: string
  size: number
  /** `Blob` 자체. `fetch` 의 `body` 로 그대로 간다. */
  blob: Blob
}

// 화면은 `message` 가 아니라 `name`·`bytes`·`status` 로 번역한다(`@maru/ui` 의 `why`).
// 클라이언트는 화면 언어를 모른다.

export class UploadTooBig extends Error {
  readonly bytes: number
  constructor(bytes: number) {
    super(
      `파일은 ${Math.floor(ATTACHMENT_MAX_BYTES / 1024 / 1024)}MiB 까지 올릴 수 있어요 (${bytes} 바이트)`,
    )
    this.name = 'UploadTooBig'
    this.bytes = bytes
  }
}

export class UploadRejected extends Error {
  readonly status: number
  constructor(status: number) {
    super(`스토리지가 파일을 받지 않았어요 (${status})`)
    this.name = 'UploadRejected'
    this.status = status
  }
}

/** 브라우저 `File` 을 그대로 넘긴다. */
export function of_file(file: File): Uploadable {
  return {
    name: file.name,
    type: file.type || 'application/octet-stream',
    size: file.size,
    blob: file,
  }
}

export async function upload_file(
  api: MaruApi,
  channel: ChannelId,
  file: Uploadable,
  put: typeof fetch = globalThis.fetch,
): Promise<Attachment> {
  if (file.size <= 0 || file.size > ATTACHMENT_MAX_BYTES) throw new UploadTooBig(file.size)
  const { attachment, upload_slot } = await api.upload_begin(channel, {
    filename: file.name,
    mime: file.type,
    bytes: file.size,
  })
  // 서명에 Content-Type 이 들어 있다. 다르게 보내면 403 이다.
  const r = await put(upload_slot, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file.blob,
  })
  if (!r.ok) {
    // 올라가지 못한 행은 지운다. 남겨 두면 "첨부를 찾을 수 없다" 가 화면에 남는다.
    await api.remove_attachment(attachment.id).catch(() => {})
    throw new UploadRejected(r.status)
  }
  return attachment
}

/** 사진(얼굴·이모지)의 공통 앞 절반 — 규칙을 보고, 자리를 받아, 같은 종류로 올린다. 번호를 돌려준다. */
async function put_picture(
  begin: (meta: { mime: string; bytes: number }) => Promise<{ id: string; upload_slot: string }>,
  file: Uploadable,
  put: typeof fetch,
): Promise<string> {
  if (file.size <= 0 || file.size > FACE_MAX_BYTES) throw new UploadTooBig(file.size)
  if (!(FACE_KINDS as readonly string[]).includes(file.type)) throw new UploadRejected(415)
  const { id, upload_slot } = await begin({ mime: file.type, bytes: file.size })
  const r = await put(upload_slot, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file.blob,
  })
  // 걸기 전이라 남는 행이 없다. 스토리지에 반쯤 올라간 객체는 아무도 가리키지 않는다.
  if (!r.ok) throw new UploadRejected(r.status)
  return id
}

/** 파일로 커스텀 이모지를 단다(관리자). 줄이기(128px)는 부르는 쪽이 한다. */
export async function upload_emoji(
  api: MaruApi,
  name: string,
  file: Uploadable,
  put: typeof fetch = globalThis.fetch,
): Promise<void> {
  const picture = await put_picture((m) => api.emoji_picture(m), file, put)
  await api.emoji_create({ name, picture })
}

/**
 * 얼굴 사진을 올리고 건다. 받을곳 주소를 돌려준다. 줄이기(256px)는 부르는 쪽(DOM 이 있는 앱)이 한다.
 * 종류가 목록 밖이거나 크면 서버에 묻지도 않고 던진다 — 서버도 같은 규칙으로 400 이다.
 */
export async function upload_face(
  api: MaruApi,
  file: Uploadable,
  put: typeof fetch = globalThis.fetch,
): Promise<string> {
  const id = await put_picture((m) => api.face_begin(m), file, put)
  return (await api.face_set(id)).url
}
